import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Slide } from '@mui/material';

const COOKIE_KEY = 'inspectiq_cookie_consent';

// Consent states: 'accepted', 'denied', or null (not yet decided)
function getConsent() {
  return localStorage.getItem(COOKIE_KEY);
}

function setConsent(value) {
  localStorage.setItem(COOKIE_KEY, value);
}

// Enable/disable Google Analytics based on consent
function updateGtagConsent(consent) {
  if (window.gtag) {
    if (consent === 'denied') {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
      });
      // Disable GA
      window['ga-disable-G-QCQWXEEK40'] = true;
    } else {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted',
      });
      window['ga-disable-G-QCQWXEEK40'] = false;
    }
  }
}

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = getConsent();

    if (consent === null) {
      // Default: accept cookies (analytics enabled), but show banner
      updateGtagConsent('accepted');
      // Show banner after a short delay
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    } else {
      // Apply saved preference
      updateGtagConsent(consent);
    }
  }, []);

  const handleAccept = () => {
    setConsent('accepted');
    updateGtagConsent('accepted');
    setVisible(false);
  };

  const handleDeny = () => {
    setConsent('denied');
    updateGtagConsent('denied');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <Slide direction="up" in={visible} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          background: 'rgba(1, 86, 122, 0.97)',
          backdropFilter: 'blur(8px)',
          color: 'white',
          px: { xs: 2, md: 4 },
          py: { xs: 2, md: 2.5 },
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          boxShadow: '0 -4px 20px rgba(0,0,0,0.2)',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            fontSize: { xs: '13px', sm: '14px' },
            lineHeight: 1.5,
            opacity: 0.95,
          }}
        >
          We use cookies to analyze site traffic and improve your experience. By continuing to browse, you accept our use of cookies.{' '}
          <a
            href="/privacy-policy"
            style={{ color: '#a5d8f0', textDecoration: 'underline' }}
          >
            Privacy Policy
          </a>
        </Typography>
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={handleDeny}
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.5)',
              fontSize: '13px',
              textTransform: 'none',
              px: 2.5,
              '&:hover': {
                borderColor: 'white',
                background: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            Decline
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleAccept}
            sx={{
              background: 'white',
              color: '#01567a',
              fontSize: '13px',
              fontWeight: 600,
              textTransform: 'none',
              px: 2.5,
              '&:hover': {
                background: '#e0f2f7',
              },
            }}
          >
            Accept Cookies
          </Button>
        </Box>
      </Box>
    </Slide>
  );
};

export default CookieConsent;
